<template>
    <div class="m-productTree" :class="{ '--subproducts': hasSubProducts }">
        <component
            v-for="(product, pKey) in products"
            class="m-productTree__product"
            :class="mainProductClass(product)"
            :id="`prod-ref-${promotion.groupId || 0}-${product.promo_dict_product_id || product.id }`"
            :is="componentName"
            :key="`product-${product.id}`"
            :product="product"
            :p-key="+product.id"
            :promo-id="promotion.id"
            :promo="promotion"
            :promotion="promotion"
            :group-idx="priceGroupIdx"
            :group-id="promotion.groupId"
            :active-key="pKey"
            :show-price="showRegularPriceInput"
            :show-price-lowest="showPriceLowest"
            :show-price-lowest-discount="showPriceLowestDiscount"
            :merchant-id="merchantId"
            :product-set="set"
            :change-by-merchants-group="changeByMerchantsGroup"
            :preview-type="previewType"
            :set-extra-fields-scroll="setExtraFieldsScroll"
            :product-set-id="productSetId"
            @has-extra-fields="$emit('has-extra-fields', $event)"
        >
            <template
                v-if="
                    product.promo_dict_product_eans_count > 1 ||
                        product.promo_dict_product?.promo_dict_product_eans_count > 1
                "
                #showMoreBtn
            >
                <the-btn
                    v-tooltip="{
                        content: 'Rozwiń aby zobaczyć inne EANy tego produktu',
                    }"
                    type="button"
                    class="m-productTree__showMore px-0"
                    :id="`showMoreBtn-${product.id}`"
                    :class="activeProductId === (product.promo_dict_product_id || product.id) ? '' : 'btn-custom-gray'"
                    @click="showMore(product)"
                >
                    <a-icon
                        :icon="
                            activeProductId === (product.promo_dict_product_id || product.id)
                                ? 'ArrowUp'
                                : 'ArrowDown'
                        "
                    />
                </the-btn>
            </template>
            <page-preloader
                v-if="activeProductId === (product.promo_dict_product_id || product.id) && subProductsLoading"
                style="max-width: 100%; flex-basis: 100%;"
                min-height="123px"
            />
            <template
                v-if="
                    activeProductId === (product.promo_dict_product_id || product.id) &&
                        product.subProducts &&
                        product.subProducts.length > 0
                "
            >
                <component
                    v-for="(subProduct, spKey) in product.subProducts"
                    class="m-productTree__subproduct"
                    :class="productClass"
                    :is="componentName"
                    :key="`subproduct-${subProduct.id}`"
                    :product="subProduct"
                    :p-key="+subProduct.id"
                    :promo-id="promotion.id"
                    :promo="promotion"
                    :promotion="promotion"
                    :group-idx="priceGroupIdx"
                    :group-id="promotion.groupId"
                    :merchant-id="merchantId"
                    :active-key="spKey"
                    :show-price="showRegularPriceInput"
                    :show-price-lowest="showPriceLowest"
                    :show-price-lowest-discount="showPriceLowestDiscount"
                    :set-weight="setWeight"
                    :product-set="set"
                    :change-by-merchants-group="changeByMerchantsGroup"
                    :product-set-id="productSetId"
                />
            </template>
        </component>
    </div>
</template>

<script>
import LabelProduct from '@/components/labels/LabelProduct'
import LabelListProduct from '@/components/labels/LabelListProduct'
import PromoProduct from '@/components/promotions/PromoProduct'
import PromotionPreviewProduct from '@/components/promotions/PromotionPreviewProduct'
import ChangePriceProduct from '@/components/promotions/ChangePriceProduct'
import ChangeShopPricesProduct from '@/components/promotions/ChangeShopPricesProduct'
import AddNewPromoProduct from '@/components/promotions/addNewPromo/AddNewPromoProduct'

export default {
    name: 'ProductTree',
    props: {
        products: {
            type: Array,
            default: () => []
        },
        componentName: {
            type: String,
            required: true
        },
        previewType: {
            type: String,
            required: false
        },
        promotion: {
            type: Object,
            default: () => {
                return {
                    id: null,
                    groupId: null
                }
            }
        },
        priceGroupIdx: {
            type: Number,
            required: false
        },
        showRegularPriceInput: {
            type: Boolean,
            default: false
        },
        productClass: {
            type: String,
            required: false
        },
        merchantId: {
            type: Number,
            required: false
        },
        selectedLabels: {
            type: Boolean,
            default: false
        },
        setWeight: {
            type: String,
            required: false
        },
        productSet: {
            type: Object,
            required: false
        },
        productSetId: {
            type: Number,
            required: false
        },
        changeByMerchantsGroup: {
            type: Boolean,
            default: false
        },
        rootMargin: {
            type: Number,
            default: -235
        },
        showPriceLowest: {
            type: Boolean,
            default: false
        },
        showPriceLowestDiscount: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            activeProductId: null,
            subProductsLoading: false
        }
    },
    components: {
        LabelProduct,
        LabelListProduct,
        PromotionPreviewProduct,
        PromoProduct,
        ChangePriceProduct,
        ChangeShopPricesProduct,
        AddNewPromoProduct
    },
    computed: {
        set() {
            if (this.productSet) {
                return this.productSet
            }
            const sets =
                this.promotion.product_sets || this.promotion.productSets || []
            return sets.find(s => s.id === this.productSetId)
        },
        hasSubProducts() {
            return this.products.some(
                product =>
                    product.promo_dict_product_eans_count > 1 ||
                    product.promo_dict_product?.promo_dict_product_eans_count > 1
            )
        }
    },
    methods: {
        async showMore(product) {
            const { subProducts } = product

            const productId = product.promo_dict_product_id || product.id

            if (!productId) {
                return
            }

            this.activeProductId = this.activeProductId === productId ? null : productId

            const hasFetchedSubProducts = subProducts?.length > 0
            if (hasFetchedSubProducts) {
                return
            }

            document.body.classList.add('--loadingSubproducts')

            this.subProductsLoading = true

            try {
                const res = await this.$store.$api('promo_dict_product_eans', {
                    params: {
                        promo_dict_product_id: productId,
                        main: false,
                        page: 1,
                        per: 100
                    }
                })

                if (!res || !res.data) {
                    document.body.classList.remove('--loadingSubproducts')
                    return
                }

                const findProduct = this.products.find(
                    p => p.id === productId || p.promo_dict_product_id === productId
                )

                if (findProduct) {
                    this.$set(findProduct, 'subProducts', res.data.data || [])
                }
                this.$emit('subproducts-fetched', {
                    productId,
                    subProducts: res.data.data || []
                })
            } catch (error) {
                console.error('Error fetching sub-products:', error)
            } finally {
                document.body.classList.remove('--loadingSubproducts')
                this.subProductsLoading = false
            }
        },

        setExtraFieldsScroll() {
            if (window.innerWidth < 768) {
                const showMoreBtn = document.querySelector(
                    `#showMoreBtn-${this.activeProductId}`
                )
                const extraFieldsBtn = document.querySelector(
                    `#m-labels__btnExtraFields-${this.activeProductId}`
                )
                if (
                    this.activeProductId &&
                    showMoreBtn &&
                    extraFieldsBtn &&
                    document.getElementById(
                        `extraField-scroll-watcher-${this.activeProductId}`
                    ) === null
                ) {
                    const scrollWatcher = document.createElement('div')
                    scrollWatcher.id = `extraField-scroll-watcher-${this.activeProductId}`
                    scrollWatcher.setAttribute('extraField-scroll-watcher', '')
                    showMoreBtn.before(scrollWatcher)
                    const navObserver = new IntersectionObserver(
                        entries => {
                            showMoreBtn.classList.toggle(
                                'extraField-sticking',
                                !entries[0].isIntersecting
                            )
                        },
                        { rootMargin: `${this.rootMargin}px 0px 0px 0px` }
                    )

                    navObserver.observe(scrollWatcher)
                }
            }
        },
        scrollToMainProduct(newId, oldId) {
            let idToScrollTo
            if (!newId && oldId) {
                idToScrollTo = oldId
            }
            if (newId && oldId) {
                idToScrollTo = newId
            }

            if (idToScrollTo) {
                const isModal = document.querySelector('.modal-content')
                const container = isModal || document.querySelector('body')

                const headerHeight = document.querySelector('header')
                    .offsetHeight
                const elToScrollTo = this.$el.querySelector(
                    `#prod-ref-${this.promotion?.groupId || 0}-${idToScrollTo}`
                )
                const isSticking = elToScrollTo.querySelector('.sticking')
                if ((isSticking && !isModal) || isModal) {
                    let duration = 10
                    if (isModal) duration = 300
                    this.$scrollTo(elToScrollTo, duration, {
                        offset: -headerHeight - 8,
                        easing: 'ease-in-out',
                        container,
                        cancelable: true
                    })
                }
            }
        },

        mainProductClass(product) {
            return [
                { 'm-productTree__hasSubproducts': product.subProducts?.length > 0 },
                { 'm-productTree__hasSubproducts--active': this.activeProductId === product.id }
            ]
        }
    },
    watch: {
        activeProductId(newId, oldId) {
            const wrapper = document.getElementById(
                `mp-promos__productWrapper-${this.activeProductId}`
            )
            if (
                this.activeProductId &&
                wrapper &&
                document.getElementById(
                    `data-scroll-watcher-${this.activeProductId}`
                ) === null
            ) {
                const scrollWatcher = document.createElement('div')
                scrollWatcher.id = `data-scroll-watcher-${this.activeProductId}`
                scrollWatcher.setAttribute('data-scroll-watcher', '')
                wrapper.after(scrollWatcher)
                const navObserver = new IntersectionObserver(
                    entries => {
                        wrapper.classList.toggle(
                            'sticking',
                            !entries[0].isIntersecting
                        )
                    },
                    { rootMargin: `${this.rootMargin}px 0px 0px 0px` }
                )

                navObserver.observe(scrollWatcher)
            }
            this.setExtraFieldsScroll()
            this.scrollToMainProduct(newId, oldId)
        }
    }
}
</script>
